import React, { useMemo, useState } from 'react';
import { SWRResponse } from 'swr';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import {
  Typography,
  Stack,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import TripMatchCard from './TripMatchCard';
import MatchCard from './MatchCard';
import { isDeclinedOrCanceled } from '../../utils/trip-matches';
import CancelSoldTripModal from '../modals/cancel-sold-trip-modal';

const isDeclinedOrCanceledStatus = (status: TripMatchStatus) => (isDeclinedOrCanceled(status) ? 1 : -1);

type TripRequestTripMatchesViewProps = {
  tripMatches: SWRResponse<TripMatch[], any, any>,
  tripRequestId?: string,
  onCancelClick: (id:string) => void,
  onClickReactivate: (id: string) => void,
  onSoldCanceled: () => void
};

const TripRequestTripMatchesView = ({
  tripMatches,
  tripRequestId,
  onCancelClick,
  onClickReactivate,
  onSoldCanceled,
}: TripRequestTripMatchesViewProps) => {
  const { data: matchesData, isLoading, error } = tripMatches;
  const [cancelSoldTripId, setCancelSoldTripId] = useState<string | null>(null);

  const sortedTripMatches = useMemo(() => {
    if (!matchesData) return [];
    return matchesData
      .slice()
      .sort((a, b) => isDeclinedOrCanceledStatus(a.status) - isDeclinedOrCanceledStatus(b.status));
  }, [matchesData]);

  return (
    <Card>
      <CardHeader title="Matches" />
      <CardContent>
        {isLoading && <TripRequestTripMatchesView.Skeleton />}

        {error && (
          <Typography variant="body1" color="error">
            There was an error loading your matches. Please try again later.
          </Typography>
        )}

        {!isLoading && !error && matchesData && matchesData.length === 0 && (
          <Typography variant="body1" color="grey.500">
            There are no partners matched to this trip.
          </Typography>
        )}

        {!isLoading && !error && sortedTripMatches.length > 0 && (
          <Stack spacing={2}>
            {sortedTripMatches.map((tripMatch) => (
              <TripMatchCard
                key={tripMatch.id}
                id={tripMatch.id}
                partnerId={tripMatch.partnerId}
                tripRequestId={tripRequestId}
                countries={tripMatch.countries}
                isAssigned={tripMatch.assignedTo !== ''}
                partnerName={tripMatch.partnerName}
                salesStage={tripMatch.salesStage}
                status={tripMatch.status}
                onCancelClick={onCancelClick}
                onClickReactivate={onClickReactivate}
                onCancelSold={setCancelSoldTripId}
                travelerId={tripMatch.travelers[0].id}
              />
            ))}
          </Stack>
        )}
      </CardContent>
      <CancelSoldTripModal
        open={cancelSoldTripId !== null}
        onClose={() => setCancelSoldTripId(null)}
        tripMatchId={cancelSoldTripId ?? ''}
        onSuccess={onSoldCanceled}
      />
    </Card>
  );
};

TripRequestTripMatchesView.Skeleton = () => (
  <Stack spacing={1.5}>
    <MatchCard.Skeleton />
    <MatchCard.Skeleton />
  </Stack>
);

export default TripRequestTripMatchesView;
